import { ReactNode, useEffect, useState } from "react";

import { UserData, addUserDataListener, getUserDataCache } from "@/hooks/useUserPermissionsSync";
import { checkUserPermissions } from "@/utils/permissionUtils";

type PermissionGuardProps = {
    children: ReactNode;
    permissions?: string[];
    fallback?: ReactNode;
    mode?: "all" | "any"; // All or any permissions
};

/**
 * Component that allows content to be displayed based on permissions
 *
 * @param children Content to display
 * @param permissions Required permissions array
 * @param fallback Content to display if no permissions (default: null)
 * @param mode Permission check mode - "all": all permissions required, "any": any permission sufficient (default: "all")
 */
export const PermissionGuard = ({
    children,
    permissions = [],
    fallback = null,
    mode = "all",
}: PermissionGuardProps) => {
    const [hasPermission, setHasPermission] = useState<boolean>(false);

    useEffect(() => {
        // Check permissions on initial load
        const initialUserData = getUserDataCache();

        const checkUserPermissionsInner = (currentUserData: UserData | null) => {
            // If user data is not available or no permissions are required
            if (!currentUserData) {
                setHasPermission(false);
                return;
            }

            // If no permissions are required
            if (!permissions.length) {
                setHasPermission(true);
                return;
            }

            // User permissions
            const userPermissions = currentUserData.permissionCodes || [];

            // Mode check - "any" or "all"
            if (mode === "any") {
                // "any" mode - having any permission is sufficient
                const hasAnyPermission = permissions.some((permission) => userPermissions.includes(permission));
                setHasPermission(hasAnyPermission);
            } else {
                // "all" mode - having all permissions is required
                setHasPermission(checkUserPermissions(userPermissions, permissions));
            }
        };

        checkUserPermissionsInner(initialUserData);

        // Listen for cache changes
        const unsubscribe = addUserDataListener(() => {
            const updatedUserData = getUserDataCache();
            checkUserPermissionsInner(updatedUserData);
        });

        return unsubscribe;
    }, [permissions, mode]);

    // If permissions are available, display content, otherwise display fallback
    return hasPermission ? <>{children}</> : <>{fallback}</>;
};

export default PermissionGuard;
